import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { setSearchParams, fetchSearchResults } from '../store/slices/searchSlice';

const CategoryTree = ({ scrollToCategoryId }) => {
  const { categories, status, error } = useSelector((state) => state.categories);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [openCategories, setOpenCategories] = useState({});
  const categoryRefs = useRef({});

  useEffect(() => {
    if (categories.length > 0) {
      const initialOpenCategories = {};
      const categoryIdFromState = location.state?.categoryId;

      if (categoryIdFromState) {
        categories.forEach((category) => {
          if (category.children) {
            const matchingChild = category.children.find(
              (child) => child.id === categoryIdFromState
            );
            if (
              matchingChild &&
              matchingChild.children &&
              matchingChild.children.length > 0
            ) {
              initialOpenCategories[matchingChild.id] = true;
            }
          }
        });
      } else {
        const firstCategory = categories[0];
        if (
          firstCategory &&
          firstCategory.children &&
          firstCategory.children.length > 0
        ) {
          initialOpenCategories[firstCategory.children[0].id] = true;
        }
      }

      setOpenCategories(initialOpenCategories);
    }
  }, [categories, location.state]);

  useEffect(() => {
    if (scrollToCategoryId && categoryRefs.current[scrollToCategoryId]) {
      categoryRefs.current[scrollToCategoryId].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [scrollToCategoryId, categories]);

  const toggleCategory = (categoryId) => {
    setOpenCategories((prevState) => ({
      ...prevState,
      [categoryId]: !prevState[categoryId],
    }));
  };

  const handleCategoryClick = (categoryId, categoryName) => {
    const searchData = {
      categoryId,
      categoryName,
      query: categoryName,
      location: "",
      lat: null,
      lng: null,
      how: [],
      when: "all_time",
      sortBy: "relevance",
      filtre: [],
    };
    dispatch(setSearchParams(searchData));
    dispatch(fetchSearchResults(searchData));
    navigate('/resultats', { replace: true });
  };

  const renderGrandchildren = (children) => (
    <div className="child-cards">
      {children.map((child) => (
        <div key={child.id} className="child-card body grey500">
          <div
            onClick={() => handleCategoryClick(child.id, child.name)}
            style={{ cursor: "pointer" }}
          >
            <span className="service body grey500">{child.name}</span>
            <img
              src="/images/smR.svg"
              alt="Flèche catégorie"
              height={16}
              width={16}
              className="arrow-category ml-8"
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderCategories = (categoryList) =>
    categoryList.map((category) => (
      <div key={category.id} className="category-section">
        <div className="parent-cat d-flex align-items-end">
          {/* <img
            className="mr-16 filtered-image-dark"
            src={`${process.env.REACT_APP_FILE_URL}${category.icon}`}
            width={32}
            height={32}
            alt={`Icone de la catégorie ${category.name}`}
          /> */}
          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: 32,
              height: 32,
            }}
            className="mr-16"
          >
            <img
              src={`${process.env.REACT_APP_FILE_URL}${category.icon}`}
              width={32}
              height={32}
              alt={`Icone de la catégorie ${category.name}`}
              style={{ display: "block" }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#202020",
                mixBlendMode: "overlay",
                opacity: 1,
              }}
            ></div>
          </div>
          <h3
            className="parent-title mb-0 mr-16 pointer"
            onClick={() => handleCategoryClick(category.id, category.name)}
          >
            {category.name}
          </h3>
          <span
            onClick={() => handleCategoryClick(category.id, category.name)}
            className="link-blue pointer"
          >
            En savoir plus
          </span>
        </div>

        {category.children && category.children.length > 0 && (
          <div className="subcategory-box mt-24">
            {category.children.map((child) => (
              <div
                key={child.id}
                className="subcategory-section"
                ref={(el) => (categoryRefs.current[child.id] = el)}
              >
                <div className="subcategory-header">
                  <h4 className="subcategory-title mb-0">
                    <div
                      onClick={() => handleCategoryClick(child.id, child.name)}
                      style={{ cursor: "pointer" }}
                    >
                      {child.name}
                    </div>
                  </h4>
                  {child.children && child.children.length > 0 && (
                    <button
                      className="toggle-button"
                      onClick={() => toggleCategory(child.id)}
                      aria-expanded={openCategories[child.id]}
                      aria-controls={`child-cards-${child.id}`}
                    >
                      {openCategories[child.id]
                        ? "Replier les catégories"
                        : "Voir les catégories"}
                    </button>
                  )}
                </div>
                {openCategories[child.id] && child.children && child.children.length > 0 && (
                  <div
                    id={`child-cards-${child.id}`}
                    className="child-cards"
                    aria-labelledby={`toggle-button-${child.id}`}
                  >
                    {renderGrandchildren(child.children)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));

  if (status === "loading") {
    return (
      <div className="category-tree">
        {[...Array(2)].map((_, index) => (
          <div key={index} className="category-skeleton">
            <Skeleton
              height={30}
              width={200}
              style={{ marginBottom: "10px" }}
            />
            {[...Array(4)].map((__, i) => (
              <Skeleton
                key={i}
                height={90}
                width={1100}
                style={{ marginBottom: "10px", borderRadius: "5px" }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (status === "failed") return <div>Error: {error}</div>;

  return <div className="category-tree mt-32">{renderCategories(categories)}</div>;
};

export default CategoryTree;
